import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OAuthService } from 'angular-oauth2-oidc';
import { tap } from 'rxjs/operators';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from '@env';
import { type CurrentUser } from '../models/user.model';
import { TableStateService } from '../services/table-state.service';

// ─── Ключ хранилища ───────────────────────────────────────────────────────────

const USER_KEY = 'current_user';

// ─── Сервис ───────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly http = inject(HttpClient);
    private readonly oauthService = inject(OAuthService);
    private readonly tableState = inject(TableStateService);

    // Флаг инициализации — чтобы интерцептор не вызывал logout при 401 во время старта
    initializing = true;

    // Signal с текущим пользователем — источник правды для всего приложения.
    // Компоненты читают его напрямую: authService.currentUser()
    readonly currentUser = signal<CurrentUser | null>(this.loadUserFromStorage());

    // ─── Keycloak: конфигурация и инициализация ──────────────────────────────────
    // Вызывается один раз из APP_INITIALIZER в app.config.ts

    async initKeycloak(): Promise<void> {
        this.oauthService.configure(environment.keycloak);
        this.oauthService.setupAutomaticSilentRefresh();

        await this.oauthService.loadDiscoveryDocumentAndTryLogin();

        // Если токен есть (вернулись после редиректа или есть в сессии) — загружаем профиль
        if (this.oauthService.hasValidAccessToken()) {
            try {
                await firstValueFrom(this.fetchCurrentUser());
            } catch (err) {
                console.error('Не удалось загрузить профиль пользователя (GET /identity/me):', err);
            }
        }
        this.initializing = false;
    }

    // ─── Вход: редирект на страницу логина Keycloak ───────────────────────────────

    login(): void {
        this.tableState.resetFiltersAndSort();
        this.oauthService.initLoginFlow();
    }

    // ─── Выход ───────────────────────────────────────────────────────────────────

    logout(): void {
        localStorage.removeItem(USER_KEY);
        this.tableState.resetFiltersAndSort();
        this.currentUser.set(null);
        this.oauthService.logOut(); // редиректит на Keycloak logout
    }

    // ─── Загрузка профиля с бэкенда (GET /identity/me) ───────────────────────────
    // Вызывается после успешного получения токена от Keycloak

    fetchCurrentUser(): Observable<CurrentUser> {
        return this.http.get<CurrentUser>(`${environment.apiUrl}/identity/me`).pipe(
            tap((user) => {
                this.saveUser(user);
                this.currentUser.set(user);
            })
        );
    }

    // ─── Токен ───────────────────────────────────────────────────────────────────

    getToken(): string | null {
        return this.oauthService.getAccessToken() ?? null;
    }

    isAuthenticated(): boolean {
        return this.oauthService.hasValidAccessToken();
    }

    // ─── Вспомогательные ─────────────────────────────────────────────────────────

    private saveUser(user: CurrentUser): void {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
    }

    // Восстанавливает пользователя из localStorage при F5,
    // чтобы не терять роль и allowedCompanyIds до завершения initKeycloak()
    private loadUserFromStorage(): CurrentUser | null {
        try {
            const raw = localStorage.getItem(USER_KEY);
            return raw ? (JSON.parse(raw) as CurrentUser) : null;
        } catch {
            return null;
        }
    }
}
